import React, { useContext, useEffect, useMemo } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate, useLocation } from "react-router-dom";
import './App.css'; // Add this for the flexbox styling
import AuthPage from "./pages/AuthPage/AuthPage";
import ProtectedRoute from "./Routing/ProtectedRoute";
import WelcomePage from "./pages/WelcomePage/WelcomePage";
import HomePage from './pages/HomePage/HomePage';
import LogoutPage from './pages/LogoutPage/LogoutPage';
import StudentHomePage from './pages/HomePage/StudentHomePage/StudentHomePage';
import { AuthContext } from "./context/AuthContext";
import Footer from './components/Footer/Footer';
import TrialPage from './pages/TrialPage/TrialPage';
import QuizReviewPage from './pages/QuizPages/QuizReviewPage/QuizReviewPage';
import UpdateQuestionPage from "./pages/QuizPages/UpdateQuestionPage/UpdateQuestionPage";
import UpdateSubquestionPage from './pages/QuizPages/UpdateSubquestionPage/UpdateSubquestionPage';
import CreateQuestionPage from './pages/QuizPages/CreateQuestionPage/CreateQuestionPage';
import CreateSubquestionPage from './pages/QuizPages/CreateSubquestionPage/CreateSubquestionPage';
import TrialQuizzesPage from "./pages/Dashboard/TrialQuizzesPage/TrialQuizzesPage";
import SessionPage from "./pages/SessionPage/SessionPage";
import SetupProfilePage from "./pages/ProfilePages/SetupProfilePage/SetupProfilePage";
import StudentDashboardPage from "./pages/Dashboard/StudentDashboardPage/StudentDashboardPage";
import FamilyDashboardPage from "./pages/Dashboard/FamilyDashboardPage/FamilyDashboardPage";
import TeacherDashboardPage from "./pages/Dashboard/TeacherDashboardPage/TeacherDashboardPage";
import SchoolDashboardPage from "./pages/Dashboard/SchoolDashboardPage/SchoolDashboardPage";
import PaymentPlaceholder from "./pages/Placeholders/PaymentPlaceholder/PaymentPlaceholder";
import CreateQuizPage from "./pages/Dashboard/CreateQuizPage/CreateQuizPage";
import ActiveSessionsPage from "./pages/ReportingPages/ActiveSessionsPage/ActiveSessionsPage";
import AttemptedUsersPage from "./pages/ReportingPages/AttemptedUsersPage/AttemptedUsersPage";
import ClassManagementPage from "./pages/Dashboard/ClassManagementPage/ClassManagementPage";
import TeacherSpecificPage from "./pages/ReportingPages/TeacherSpecificPage/TeacherSpecificPage";
import QuizSpecificPage from "./pages/ReportingPages/QuizSpecificPage/QuizSpecificPage";
import StudentClassPage from "./pages/Dashboard/StudentClassPage/StudentClassPage";
import PaymentPage from "./pages/PaymentPages/PaymentPage/PaymentPage";
import SuspendedUserPage from "./pages/SuspendedUserPage/SuspendedUserPage";
import PurchasePlanPage from "./pages/PurchasePlanPage/PurchasePlanPage";
import useAxios from "./hooks/useAxios";
import StatusUpdater from "./Routing/StatusUpdater";
import ProfilePage from "./pages/ProfilePage/ProfilePage";
import GlobalBackButton from "./components/common/GlobalBackButton/GlobalBackButton";
import GlobalHomeButton from "./components/common/GlobalHomeButton/GlobalHomeButton";

function getInitialRoute(token, role, status) {
  if (!token) return "/";
  if (token){
    if (status === "active") {
     return "/dashboard";
    } else if (status === "trial") {
      return "/home"
    } else if (status === "suspended") {
      return "/suspended";
    }
    
  } 
  // if (role === "student") {
  //   if (status === "pending") return "/setup-profile";
  //   if (status === "active") return "/dashboard";
  // } else {
  //   if (status === "trial") return "/home";
  //   if (status === "active") return "/dashboard";
  // }
}

const RedirectHandler = () => {
  const { status } = useContext(AuthContext);
  const navigate = useNavigate();

  React.useEffect(() => {
    if (status === "suspended") {
      navigate("/suspended");
    }
  }, [status, navigate]);

  return null;
};

function App() {
  const { token, role, status } = useContext(AuthContext);
  const initialRoute = useMemo(() => getInitialRoute(token, role, status), [role, status, token]);

  return (
    <div className="app-container">
      <Router>
      <StatusUpdater />
      <RedirectHandler /> 
      <GlobalBackButton />
      <GlobalHomeButton />
        <div className="content-container">
          <Routes>
            <Route path="/auth" element={<AuthPage />} />

            {/* Root route: If user has no token, show WelcomePage; 
                if they do have a token (already authenticated), redirect to initialRoute */}
            <Route 
              path="/" 
              element={
                token 
                  ? <Navigate to={initialRoute} replace /> 
                  : <WelcomePage />
              } 
            />
            <Route 
              path="/profile" 
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/dashboard" 
              element={
                <ProtectedRoute>
                  {role === 'student' && <StudentDashboardPage />}
                  {role === 'family' && <FamilyDashboardPage />}
                  {role === 'teacher' && <TeacherDashboardPage />}
                  {role === 'school' && <SchoolDashboardPage />}
                </ProtectedRoute>
              }
            />
            <Route 
              path="/logout" 
              element={
                <ProtectedRoute>
                  <LogoutPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/home" 
              element={
                <ProtectedRoute>
                  <HomePage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/payment" 
              element={
                <ProtectedRoute>
                  <PaymentPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/free-trial" 
              element={
                <ProtectedRoute>
                  <TrialPage />
                </ProtectedRoute>
              } 
            />
            <Route 
              path="/quiz-review/:id" 
              element={
              <ProtectedRoute>
                <QuizReviewPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/update-question/:id" 
              element={
              <ProtectedRoute>
                <UpdateQuestionPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/update-subquestion/:questionId/:subquestionId" 
              element={
              <ProtectedRoute>
                <UpdateSubquestionPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/quiz/:quizId/add-question" 
              element={
              <ProtectedRoute>
                <CreateQuestionPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/questions/:questionId/add-subquestion" 
              element={
              <ProtectedRoute>
                <CreateSubquestionPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/owner-quizzes" 
              element={
              <ProtectedRoute>
                <TrialQuizzesPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/sessions/:id" 
              element={
              <ProtectedRoute>
                <SessionPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/setup-profile" 
              element={
              <ProtectedRoute>
                <SetupProfilePage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/quiz/create" 
              element={
              <ProtectedRoute>
                <CreateQuizPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/reporting/active-sessions" 
              element={
              <ProtectedRoute>
                <ActiveSessionsPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/reporting/attempted-users" 
              element={
              <ProtectedRoute>
                <AttemptedUsersPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/classes/:classId" 
              element={
              <ProtectedRoute>
                <ClassManagementPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/reporting/teacher-specific" 
              element={
              <ProtectedRoute>
                <TeacherSpecificPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/reporting/quiz-specific" 
              element={
              <ProtectedRoute>
                <QuizSpecificPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/student/class/:classId" 
              element={
              <ProtectedRoute>
                <StudentClassPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/suspended" 
              element={
              <ProtectedRoute>
                <SuspendedUserPage />
              </ProtectedRoute>
              } 
            />
            <Route 
              path="/purchase-plan" 
              element={
              <ProtectedRoute>
                <PurchasePlanPage />
              </ProtectedRoute>
              } 
            />
          </Routes>
        </div>
        <Footer />
      </Router>
    </div>
  );
}

export default App;
